import React, { Component } from 'react'
import { Button, FormInstance, Steps } from 'antd'

export type Step = {
  key: string
  title: string
  /** Field names validated before leaving this step. */
  fields: string[]
  content: () => React.ReactNode
}

type MappingStepperProps = {
  form: FormInstance
  /** Steps to render, computed by the owning form from its own state. */
  steps: Step[]
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingStepperState = {
  current: number
}

/**
 * Shared stepped-wizard shell for every mapping form. Each form computes its own
 * `steps` (fields + content) from whatever form-specific state it tracks
 * (direction, type, scope, …) and hands them here. This component owns only the
 * generic machinery: the Steps header, keeping every step mounted so field values
 * survive navigation, forward-validation, and the Back/Next/Save footer.
 *
 * The step list may change between renders (e.g. switching a mapping type adds or
 * removes steps); `safeCurrent` clamps the tracked index so it stays in range.
 */
export default class MappingStepper extends Component<MappingStepperProps, MappingStepperState> {
  state: MappingStepperState = { current: 0 }

  /**
   * Navigate to a step. Moving backward is always allowed; moving forward
   * validates the fields of every step being skipped over so the user can't
   * jump past required input.
   */
  goTo = async (index: number) => {
    const { form, steps } = this.props
    const { current } = this.state

    if (index === current) return

    if (index < current) {
      this.setState({ current: index })
      return
    }

    const fields = steps.slice(current, index).flatMap(step => step.fields)

    try {
      if (fields.length) await form.validateFields(fields)
      this.setState({ current: index })
    } catch {
      // Validation errors are surfaced inline on the fields.
    }
  }

  next = () => this.goTo(this.state.current + 1)

  prev = () => this.setState(state => ({ current: Math.max(0, state.current - 1) }))

  handleSave = () => this.props.form.submit()

  render() {
    const { steps, loading } = this.props
    const { current } = this.state
    const safeCurrent = Math.min(current, steps.length - 1)
    const isLast = safeCurrent === steps.length - 1

    return (
      <>
        <Steps
          current={safeCurrent}
          size="small"
          style={{ marginBottom: 24, userSelect: 'none' }}
          onChange={this.goTo}
        >
          {steps.map(step => (
            <Steps.Step key={step.key} title={step.title} />
          ))}
        </Steps>

        {/* Keep every step mounted (toggle visibility) so field values survive navigation. */}
        {steps.map((step, index) => (
          <div key={step.key} style={{ display: index === safeCurrent ? 'block' : 'none' }}>
            {step.content()}
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
          <Button disabled={safeCurrent === 0} onClick={this.prev}>
            Back
          </Button>
          {isLast ? (
            <Button type="primary" loading={loading} onClick={this.handleSave}>
              Save
            </Button>
          ) : (
            <Button type="primary" onClick={this.next}>
              Next
            </Button>
          )}
        </div>
      </>
    )
  }
}
